import { Link, useParams } from 'react-router-dom';
import SiteLayout, { PortalShell, Breadcrumb } from '@/components/feature/PageShell';
import MediaCard from '@/components/feature/MediaCard';
import { topicList } from '@/mocks/news';
import { mediaList } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function TopicPage() {
  const { id = '' } = useParams();
  const topic = topicList.find((item) => item.id === id);

  usePageMeta({
    title: topic ? `${topic.title} - 菠萝蜜网专题` : '影视专题 - 菠萝蜜网',
    description: topic ? topic.description.slice(0, 90) : '菠萝蜜网影视专题集合',
    keywords: topic ? `${topic.title},影视专题,片单推荐` : '影视专题,片单',
    path: `/topic/${id}`,
  });

  if (!topic) {
    return (
      <SiteLayout>
        <PortalShell>
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
            <h1 className="font-heading text-xl font-bold text-foreground-950">专题不存在</h1>
            <Link to="/" className="mt-4 inline-block whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50">
              返回首页
            </Link>
          </div>
        </PortalShell>
      </SiteLayout>
    );
  }

  const others = topicList.filter((item) => item.id !== topic.id);

  return (
    <SiteLayout>
      <PortalShell>
        <Breadcrumb items={[{ label: '影视专题' }, { label: topic.title }]} />

        <section className="relative overflow-hidden rounded-xl bg-secondary-950">
          <div className="relative h-[200px] w-full md:h-[260px]">
            <img src={topic.cover} alt={`${topic.title} 专题封面`} title={topic.title} className="h-full w-full object-cover opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/95 via-secondary-950/70 to-transparent" />
          </div>
          <div className="absolute inset-0 flex flex-col justify-center p-5 md:p-8">
            <p className="text-[11px] tracking-[0.2em] text-accent-400">{topic.subtitle}</p>
            <h1 className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-3xl">{topic.title}</h1>
            <p className="mt-2 max-w-xl text-xs leading-relaxed text-background-200/80 md:text-sm">{topic.description}</p>
          </div>
        </section>

        <section className="mt-6">
          <div className="mb-4 flex items-end gap-3">
            <h2 className="font-heading text-lg font-bold text-foreground-950">专题片单</h2>
            <p className="pb-1 text-xs text-foreground-500">共 {topic.mediaIds.length} 部作品</p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
            {topic.mediaIds.map((mediaId, index) => (
              <MediaCard key={mediaId} id={mediaId} index={index} />
            ))}
          </div>
        </section>

        <section className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">编辑说明</h2>
          <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
            这个专题由内容编辑部整理，作品顺序按观看体验的节奏排布：先看一两部节奏明快的，再慢慢进入更安静的作品。我们的入选标准只有一条——看完之后你还会想起它。当前专题中的作品资料均为演示数据。
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {mediaList
              .filter((item) => topic.mediaIds.includes(item.id))
              .flatMap((item) => item.genres)
              .filter((genre, index, arr) => arr.indexOf(genre) === index)
              .slice(0, 8)
              .map((genre) => (
                <Link key={genre} to={`/list/${encodeURIComponent(genre)}`} className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] text-secondary-900 transition hover:bg-secondary-200">
                  {genre}
                </Link>
              ))}
          </div>
        </section>

        <section className="mt-6">
          <h2 className="mb-3 font-heading text-base font-bold text-foreground-950">其他专题</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {others.map((item) => (
              <Link key={item.id} to={`/topic/${item.id}`} className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition hover:border-primary-300">
                <div className="h-[100px] w-full overflow-hidden">
                  <img src={item.cover} alt={item.title} title={item.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                </div>
                <div className="p-3">
                  <p className="text-[10px] text-foreground-400">{item.subtitle}</p>
                  <p className="mt-0.5 truncate text-sm font-medium text-foreground-900">{item.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </PortalShell>
    </SiteLayout>
  );
}